/**
 * LeadershipSprintPage — public informational page + signup for the Summer
 * Leadership & Innovation Sprint (Round 41).
 *
 * Content is now driven by the Summer Sprint brochure — a 4-week live online
 * cohort for educators. The page mirrors the POD workflow: informational hero,
 * curriculum modules, benefits, tiered pricing, and a public signup form that
 * captures interest into ``db.sprint_signups`` (no LMS enrolment yet — the
 * Sprint is a distinct entity that will get its own learning workflow in a
 * later round). Every text block still falls back to the Public Site CMS so
 * admins can edit copy without a code deploy.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import api from "../lib/api";
import { ArrowRight, CalendarBlank, EnvelopeSimple, Sparkle, GraduationCap, Trophy, Users, ChartLineUp, Certificate, Rocket } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CURRICULUM = [
  {
    week: "Week 1",
    title: "The Smart Educator: AI, Automation & Digital Creativity",
    points: [
      "Create engaging AI avatar videos for lessons, school announcements and parent communication.",
      "Design animated teaching videos that boost learner engagement.",
      "Produce short educational videos suitable for classroom use and social media.",
      "Use Asana to organise teaching tasks, projects, events and professional responsibilities.",
    ],
  },
  {
    week: "Week 2",
    title: "Leadership, Personal Branding & Career Growth",
    points: [
      "Build leadership capacity that extends beyond the classroom.",
      "Strengthen emotional intelligence and communication skills.",
      "Develop a compelling, achievement-focused professional CV.",
      "Optimise your LinkedIn profile to attract opportunities.",
      "Position yourself deliberately for promotions and leadership roles.",
    ],
  },
  {
    week: "Week 3",
    title: "Teaching Beyond the Classroom: Innovation & Income Opportunities",
    points: [
      "Identify credible additional income opportunities available to educators.",
      "Explore consulting, tutoring, curriculum development and content creation as income paths.",
      "Discover grants, fellowships and proposal opportunities open to educators.",
      "Develop an education-based product or service of your own.",
    ],
  },
  {
    week: "Week 4",
    title: "School Improvement & Becoming an Irreplaceable Educator",
    points: [
      "Lead classroom and school improvement initiatives with confidence.",
      "Apply quality assurance practices that improve learning outcomes.",
      "Strengthen parent and stakeholder engagement.",
      "Develop practical strategies for creating measurable impact in your school.",
    ],
  },
];

const WEEKLY_RHYTHM = [
  { day: "Monday",    label: "Live Masterclass (90 min) — core concept from an expert facilitator." },
  { day: "Tuesday",   label: "Practical Assignment & Resource Pack — apply the concept immediately." },
  { day: "Wednesday", label: "Implementation Clinic — live Q&A and hands-on coaching." },
  { day: "Thursday",  label: "Leadership Pod Accountability Session — peer check-in and momentum." },
  { day: "Friday",    label: "Project Showcase, Feedback & Networking — share your work, get recognised." },
];

const TIERS = [
  { key: "early_bird", label: "Early Bird", amount: 20000, blurb: "Best value — register early and save.",  featured: true  },
  { key: "regular",    label: "Regular",    amount: 30000, blurb: "Standard rate.",                          featured: false },
  { key: "late",       label: "Late",       amount: 35000, blurb: "Final window before the Sprint begins.",  featured: false },
];

const AUDIENCE = [
  "Teachers", "School Leaders", "Heads of Department", "Vice Principals", "School Administrators",
  "School Owners", "Education Consultants", "Aspiring School Leaders", "Education Entrepreneurs",
];

export default function LeadershipSprintPage() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", school_name: "", role: "", tier: "early_bird", notes: "" });
  const [saving, setSaving] = useState(false);
  const [heroBlock, setHeroBlock] = useState(null);
  const [contactBlock, setContactBlock] = useState(null);

  useEffect(() => {
    api.get("/site-content/leadership_sprint_hero").then((r) => setHeroBlock(r.data)).catch(() => {});
    api.get("/site-content/leadership_sprint_contact").then((r) => setContactBlock(r.data)).catch(() => {});
  }, []);

  const submitSignup = async () => {
    if (!form.full_name || !form.email) {
      toast.error("Please provide at least your name and email.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leadership-sprint/signup", form);
      toast.success("You're on the Sprint list! Watch your inbox for cohort details.");
      setSignupOpen(false);
      setForm({ full_name: "", email: "", phone: "", school_name: "", role: "", tier: "early_bird", notes: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your registration. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="leadership-sprint-page">
      <PublicNav />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fbbf24 0, transparent 40%), radial-gradient(circle at 80% 80%, #a855f7 0, transparent 40%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-20">
          <p className="uppercase tracking-widest text-[10px] text-amber-300 font-bold mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> School Builders&apos; Lab · Summer Sprint
          </p>
          <h1 className="text-3xl md:text-6xl font-bold leading-tight max-w-4xl" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="sprint-hero-heading">
            {heroBlock?.heading || "Summer Leadership & Innovation Sprint"}
          </h1>
          <p className="mt-4 text-lg md:text-2xl text-amber-300 font-semibold" data-testid="sprint-tagline">
            Empowering Educators to Lead, Innovate and Thrive.
          </p>
          <p className="mt-4 text-sm md:text-base text-slate-300 max-w-2xl">
            {heroBlock?.subheading || "A high-intensity 4-week live online cohort for educators ready to grow as leaders, innovate in their classrooms and open new income doors."}
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl text-sm">
            <div className="bg-white/5 border border-white/10 p-3">
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Duration</p>
              <p className="mt-1 text-white font-semibold">4 weeks</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-3">
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Format</p>
              <p className="mt-1 text-white font-semibold">Live online</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-3">
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Starting from</p>
              <p className="mt-1 text-white font-semibold">₦20,000</p>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => setSignupOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm rounded-none"
              data-testid="sprint-hero-cta"
            >
              Register for the Sprint <ArrowRight size={14} weight="bold" />
            </button>
            <a href="#curriculum" className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white hover:bg-white/10 text-sm rounded-none">
              See the curriculum
            </a>
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16" data-testid="sprint-objectives">
        <p className="uppercase tracking-widest text-[10px] text-slate-500 font-bold">01 · By the end of the Sprint</p>
        <h2 className="text-2xl md:text-4xl font-bold text-slate-950 mt-2 max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
          You&apos;ll finish with real work, not just notes.
        </h2>
        <ul className="mt-6 grid md:grid-cols-2 gap-4">
          {[
            { icon: Rocket, text: "Use digital tools and AI to dramatically improve your productivity, communication and classroom engagement." },
            { icon: ChartLineUp, text: "Lead with greater confidence and influence in your classroom, department and school." },
            { icon: Certificate, text: "Present a professional brand — CV, LinkedIn, personal positioning — that opens doors." },
            { icon: Trophy, text: "Identify and act on realistic additional income streams within the education sector." },
            { icon: GraduationCap, text: "Design and lead a credible school or classroom improvement initiative." },
            { icon: Users, text: "Belong to a growing, accountable network of educators shaping the future of education." },
          ].map((o, i) => (
            <li key={i} className="flex items-start gap-3 bg-white border border-slate-200 p-4">
              <o.icon size={22} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-800 leading-relaxed">{o.text}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Curriculum */}
      <section id="curriculum" className="bg-slate-950 text-white" data-testid="sprint-curriculum">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
          <p className="uppercase tracking-widest text-[10px] text-amber-300 font-bold">02 · The 4-week curriculum</p>
          <h2 className="text-2xl md:text-4xl font-bold mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Four weeks. Four transformative modules.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {CURRICULUM.map((m) => (
              <div key={m.week} className="bg-white/5 border border-white/10 p-5">
                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{m.week}</p>
                <h3 className="text-lg font-bold text-white mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.title}</h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-300">
                  {m.points.map((p, i) => (
                    <li key={i} className="flex gap-2"><span className="text-amber-300">›</span><span>{p}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly rhythm */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16" data-testid="sprint-rhythm">
        <p className="uppercase tracking-widest text-[10px] text-slate-500 font-bold">03 · Weekly rhythm</p>
        <h2 className="text-2xl md:text-4xl font-bold text-slate-950 mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          A predictable weekly cadence to build momentum.
        </h2>
        <div className="mt-6 grid md:grid-cols-5 gap-3">
          {WEEKLY_RHYTHM.map((r) => (
            <div key={r.day} className="border border-slate-200 bg-white p-4">
              <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">{r.day}</p>
              <p className="mt-2 text-sm text-slate-800 leading-relaxed">{r.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fees */}
      <section id="register" className="bg-amber-50 border-y border-amber-200" data-testid="sprint-registration">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
          <p className="uppercase tracking-widest text-[10px] text-amber-700 font-bold">04 · Registration</p>
          <h2 className="text-2xl md:text-4xl font-bold text-slate-950 mt-2 max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Choose the tier that fits — save by registering early.
          </h2>
          <p className="mt-2 text-sm text-slate-700 max-w-2xl">Fees cover all live sessions, resource packs, templates, coaching and a Certificate of Completion.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {TIERS.map((t) => (
              <div key={t.key} className={`p-6 bg-white border-2 ${t.featured ? "border-amber-500" : "border-slate-200"}`} data-testid={`sprint-tier-${t.key}`}>
                {t.featured && <p className="inline-block text-[10px] uppercase tracking-widest text-amber-700 font-bold bg-amber-100 px-2 py-0.5 mb-2">Recommended</p>}
                <p className="text-sm text-slate-500">{t.label}</p>
                <p className="text-3xl md:text-4xl font-bold text-slate-950 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>₦{t.amount.toLocaleString()}</p>
                <p className="text-xs text-slate-600 mt-2">{t.blurb}</p>
                <button
                  onClick={() => { setForm((f) => ({ ...f, tier: t.key })); setSignupOpen(true); }}
                  className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm rounded-none ${t.featured ? "bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" : "border border-slate-300 hover:bg-slate-50 text-slate-800"}`}
                  data-testid={`sprint-tier-cta-${t.key}`}
                >
                  Register on this tier <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-16" data-testid="sprint-audience">
        <p className="uppercase tracking-widest text-[10px] text-slate-500 font-bold">05 · Who it&apos;s for</p>
        <h2 className="text-2xl md:text-4xl font-bold text-slate-950 mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Built for educators serious about growth.
        </h2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {AUDIENCE.map((a) => (
            <li key={a} className="px-3 py-1.5 bg-slate-900 text-slate-100 text-xs">{a}</li>
          ))}
        </ul>
      </section>

      {/* Contact */}
      <section className="bg-slate-950 text-slate-200" data-testid="sprint-contact">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="uppercase tracking-widest text-[10px] text-amber-300 font-bold">06 · Get in touch</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-2 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <EnvelopeSimple size={22} className="text-amber-400" /> {contactBlock?.heading || "learning@schoolbuilderlab.net.ng"}
            </h2>
            <p className="text-sm text-slate-400 mt-2">{contactBlock?.subheading || "Talk to admissions about the next Sprint cohort."}</p>
          </div>
          <div className="flex md:justify-end">
            <button
              onClick={() => setSignupOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm rounded-none"
              data-testid="sprint-final-cta"
            >
              Register for the Sprint <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      <PublicFooter />

      {/* Signup drawer */}
      {signupOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSignupOpen(false)}>
          <div className="bg-white max-w-lg w-full p-6 rounded-none" onClick={(e) => e.stopPropagation()} data-testid="sprint-signup-form">
            <p className="uppercase tracking-widest text-[10px] text-amber-700 font-bold flex items-center gap-2"><Sparkle size={12} weight="fill" /> Summer Sprint · Registration</p>
            <h3 className="text-xl font-bold text-slate-950 mt-1 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Reserve your seat</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Full name</Label>
                <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-none" data-testid="sprint-form-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" data-testid="sprint-form-email" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" data-testid="sprint-form-phone" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">School / Employer</Label>
                  <Input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} className="rounded-none" data-testid="sprint-form-school" />
                </div>
                <div>
                  <Label className="text-xs">Your role</Label>
                  <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-none" placeholder="e.g. Head of Department" data-testid="sprint-form-role" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Preferred tier</Label>
                <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full mt-1 border border-slate-300 rounded-none px-3 py-2 text-sm bg-white" data-testid="sprint-form-tier">
                  {TIERS.map((t) => <option key={t.key} value={t.key}>{t.label} — ₦{t.amount.toLocaleString()}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Anything we should know? (optional)</Label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full mt-1 border border-slate-300 rounded-none px-3 py-2 text-sm min-h-[80px]" data-testid="sprint-form-notes" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" className="rounded-none text-sm" onClick={() => setSignupOpen(false)}>Cancel</Button>
              <Button
                onClick={submitSignup}
                disabled={saving}
                className="rounded-none text-sm bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"
                data-testid="sprint-form-submit"
              >
                {saving ? "Saving…" : "Reserve my seat"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
